import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminShell from './AdminShell'
import Toggle from '../../components/Toggle'
import { tournamentApi } from '../../services/api'

/**
 * Admin landing — quick-glance counts + shortcut tiles.
 */
export default function AdminDashboard() {
  const [counts, setCounts] = useState({ accounts: 0, pendingInvites: 0, fees: 0 })
  const [loading, setLoading] = useState(true)
  const [cache, setCache] = useState(null)
  const [hotelCache, setHotelCache] = useState(null)
  const [showCache, setShowCache] = useState(false)
  const [showEnv, setShowEnv] = useState(false)
  const [bundleLive, setBundleLive] = useState(null) // null = loading
  const [bundleSaving, setBundleSaving] = useState(false)
  const [refundActionable, setRefundActionable] = useState(0)
  const [coursePay, setCoursePay] = useState({ unpaid: 0, overdue: 0 })

  useEffect(() => {
    tournamentApi.get('/admin/resellers/refund-queue/summary')
      .then(r => setRefundActionable(Number(r.data.actionable) || 0)).catch(() => {})
    tournamentApi.get('/admin/resellers/course-payments', { params: { status: 'unpaid', pageSize: 1 } })
      .then(r => setCoursePay(r.data.counts || { unpaid: 0, overdue: 0 })).catch(() => {})
  }, [])

  useEffect(() => {
    Promise.all([
      tournamentApi.get('/admin/resellers/accounts').catch(() => ({ data: { accounts: [] } })),
      tournamentApi.get('/admin/resellers/invites?status=pending').catch(() => ({ data: { invites: [] } })),
      tournamentApi.get('/admin/fee-settings').catch(() => ({ data: { fees: [] } })),
    ]).then(([a, i, f]) => {
      setCounts({
        accounts: a.data.accounts?.length || 0,
        pendingInvites: i.data.invites?.length || 0,
        fees: f.data.fees?.length || 0,
      })
      setLoading(false)
    })
    tournamentApi.get('/admin/course-integrations/tee-cache/health')
      .then(r => setCache(r.data)).catch(() => setCache({ error: true }))
    tournamentApi.get('/admin/hotel-integrations/cache-health')
      .then(r => setHotelCache(r.data)).catch(() => setHotelCache({ error: true }))
    tournamentApi.get('/system-settings/bundle-checkout')
      .then(r => setBundleLive(!!r.data.enabled)).catch(() => setBundleLive(false))
  }, [])

  async function saveBundleLive(next) {
    const prev = bundleLive
    setBundleLive(next) // optimistic
    setBundleSaving(true)
    try {
      const { data } = await tournamentApi.put('/system-settings/bundle-checkout', { enabled: next })
      setBundleLive(!!data.enabled)
    } catch (e) {
      setBundleLive(prev)
      alert(e?.response?.data?.message || e.message)
    } finally { setBundleSaving(false) }
  }

  return (
    <AdminShell title="Admin Dashboard">
      {refundActionable > 0 && (
        <Link to="/admin/refund-queue" style={{
          display: 'flex', alignItems: 'center', gap: 12, textDecoration: 'none',
          padding: '14px 18px', marginBottom: 16, borderRadius: 10,
          background: '#fffbeb', border: '1px solid #fde68a',
        }}>
          <span style={{ fontSize: 22 }}>⚠</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#92400e' }}>
              {refundActionable} refund{refundActionable === 1 ? '' : 's'} need{refundActionable === 1 ? 's' : ''} your action
            </div>
            <div style={{ fontSize: 12.5, color: '#a16207' }}>Cancel the hotel/golf with the providers, record the money recovered, and release the customer refund.</div>
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: '#92400e' }}>Open Refund Queue →</span>
        </Link>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16 }}>
        <Tile to="/admin/reseller-accounts"  label="Reseller Accounts" value={loading ? '…' : counts.accounts} />
        <Tile to="/admin/reseller-invites"   label="Pending Invites"   value={loading ? '…' : counts.pendingInvites} />
        <Tile to="/admin/fee-settings"       label="Fee Settings"      value={loading ? '…' : counts.fees} />
        <Tile to="/admin/course-payments"    label={coursePay.overdue > 0 ? `Course Payments (${coursePay.overdue} overdue)` : 'Course Payments'} value={coursePay.unpaid} />
        <CacheTile cache={cache} onClick={() => setShowCache(true)} />
        <HotelCacheTile health={hotelCache} />
        <EnvTile onClick={() => setShowEnv(true)} />
      </div>

      {/* Global storefront controls */}
      <div style={{ marginTop: 24, padding: 20, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 280 }}>
          <h3 style={{ margin: '0 0 4px', fontSize: 16, color: '#0f172a' }}>Bundle online purchasing</h3>
          <p style={{ margin: 0, color: '#64748b', fontSize: 13, lineHeight: 1.5, maxWidth: 640 }}>
            When ON, customers can buy prepaid-round bundles online and their card is charged. When OFF, bundle
            stores stay previewable but show “coming soon.” Each reseller still needs a configured payment gateway
            to accept Mode B payments.
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, fontWeight: 700, color: bundleLive ? '#166534' : '#94a3b8', minWidth: 28, textAlign: 'right' }}>
            {bundleLive == null ? '…' : bundleLive ? 'ON' : 'OFF'}
          </span>
          <Toggle checked={!!bundleLive} onChange={saveBundleLive} disabled={bundleLive == null || bundleSaving} size="lg" />
        </div>
      </div>

      <div style={{ marginTop: 24, padding: 20, background: '#fff', borderRadius: 8, border: '1px solid #e2e8f0' }}>
        <h3 style={{ margin: '0 0 12px', fontSize: 16, color: '#0f172a' }}>Reseller Platform v1</h3>
        <p style={{ margin: 0, color: '#64748b', fontSize: 14, lineHeight: 1.6 }}>
          Manage invite-only Reseller accounts for tee-time resellers building travel packages across many courses.
          Each reseller can connect courses, build geographic groupings, design packages, and embed iframes on their own domain.
          Use the navigation on the left to invite a new reseller, view existing accounts, or adjust platform fees.
        </p>
      </div>

      {showCache && <CacheDetailModal onClose={() => setShowCache(false)} />}
      {showEnv && <EnvModal onClose={() => setShowEnv(false)} />}
    </AdminShell>
  )
}

function Tile({ to, label, value }) {
  return (
    <Link to={to} style={{
      display: 'block', padding: 24, background: '#fff', borderRadius: 8,
      border: '1px solid #e2e8f0', textDecoration: 'none',
      transition: 'transform 0.1s, box-shadow 0.1s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)' }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
    >
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#0f172a' }}>{value}</div>
    </Link>
  )
}

// Summary tile for the tee-time availability cache — click to drill down.
function CacheTile({ cache, onClick }) {
  const loading = !cache
  const err = cache?.error
  const warmOn = cache?.warm?.enabled
  const hitRate = cache?.today?.hitRate
  const big = err ? '—' : (hitRate != null ? `${hitRate}%` : (cache?.coverage?.coveragePct != null ? `${cache.coverage.coveragePct}%` : '—'))
  const bigLabel = err ? '' : (hitRate != null ? 'hit rate today' : 'course coverage')
  return (
    <button onClick={onClick} style={{
      textAlign: 'left', cursor: 'pointer', padding: 24, background: '#fff', borderRadius: 8,
      border: '1px solid #e2e8f0', transition: 'transform 0.1s, box-shadow 0.1s', font: 'inherit',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)' }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>Tee-time cache</div>
        {!loading && !err && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
            background: warmOn ? '#dcfce7' : '#f1f5f9', color: warmOn ? '#166534' : '#64748b' }}>
            warm {warmOn ? 'ON' : 'OFF'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#0f172a' }}>{loading ? '…' : big}</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
        {loading ? 'loading…' : err ? 'unavailable' :
          `${bigLabel} · ${cache.coverage.cachedCourses}/${cache.coverage.bookableCourses} courses · ${cache.today.calls} calls today`}
      </div>
    </button>
  )
}

// Summary tile for the hotel search cache — the RateHawk "flapping watch". The
// full panel lives on the Hotel Integrations page, so this tile links there.
// Headline = RateHawk 429s (should sit at 0 on the by-ID path); amber if 429s or
// empty cells appear.
function HotelCacheTile({ health }) {
  const loading = !health
  const err = health?.error
  const rh = health?.ratehawk || {}
  const sc = health?.searchCache || {}
  const ri = health?.regionIds || {}
  const attempts = (rh.calls || 0) + (rh.timeouts || 0)
  const timeoutRate = attempts ? Math.round(((rh.timeouts || 0) / attempts) * 100) : 0
  const flap = (rh.http429 || 0) > 0 || (sc.emptyCells || 0) > 0 || timeoutRate >= 10
  return (
    <Link to="/admin/hotel-integrations" style={{
      display: 'block', padding: 24, background: '#fff', borderRadius: 8,
      border: '1px solid #e2e8f0', textDecoration: 'none', transition: 'transform 0.1s, box-shadow 0.1s',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)' }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
        <div style={{ fontSize: 13, color: '#64748b' }}>Hotel cache</div>
        {!loading && !err && (
          <span style={{ fontSize: 11, fontWeight: 700, padding: '2px 7px', borderRadius: 999,
            background: flap ? '#fef3c7' : '#dcfce7', color: flap ? '#92400e' : '#166534' }}>
            {flap ? 'watch' : 'OK'}
          </span>
        )}
      </div>
      <div style={{ fontSize: 32, fontWeight: 700, color: err ? '#0f172a' : flap ? '#b45309' : '#0f172a' }}>
        {loading ? '…' : err ? '—' : (rh.http429 ?? 0)}
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
        {loading ? 'loading…' : err ? 'unavailable' :
          `RateHawk 429s · ${rh.timeouts ?? 0} timeouts · ${ri.freshRegions ?? 0}/${ri.regions ?? 0} regions by-ID`}
      </div>
    </Link>
  )
}

// Small tile that opens the environment / config snapshot modal. Secrets are
// masked server-side; this is only a click target.
function EnvTile({ onClick }) {
  return (
    <button onClick={onClick} style={{
      textAlign: 'left', cursor: 'pointer', padding: 24, background: '#fff', borderRadius: 8,
      border: '1px solid #e2e8f0', transition: 'transform 0.1s, box-shadow 0.1s', font: 'inherit',
    }}
    onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.06)' }}
    onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none' }}
    >
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 6 }}>Environment</div>
      <div style={{ fontSize: 32, fontWeight: 700, color: '#0f172a' }}>⚙</div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>server config &amp; flags (secrets masked)</div>
    </button>
  )
}

const OUTCOME_COLOR = { hit: '#16a34a', miss: '#b45309', stale: '#b91c1c', fresh: '#2563eb' }
const fmt = (d) => { if (!d) return '—'; try { return new Date(d).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' } }

// One "problem" bucket in the cache-health modal: a titled, scrollable list of
// the courses behind a coverage/quality gap so the admin can act on names, not
// just a percentage.
function ProblemList({ title, hint, items, color }) {
  if (!items?.length) return null
  return (
    <div style={{ border: '1px solid #eef2f7', borderRadius: 6, padding: '8px 10px' }}>
      <div style={{ fontSize: 12.5, fontWeight: 600, color }}>{title} <span style={{ color: '#94a3b8' }}>({items.length})</span></div>
      <div style={{ fontSize: 11, color: '#94a3b8', margin: '2px 0 6px' }}>{hint}</div>
      <div style={{ maxHeight: 160, overflowY: 'auto' }}>
        {items.map(it => (
          <div key={it.coursesId} style={{ fontSize: 12.5, color: '#334155', padding: '2px 0', lineHeight: 1.35 }}>
            {it.courseName} <span style={{ color: '#cbd5e1' }}>#{it.coursesId}</span>
            {it.city ? <span style={{ color: '#94a3b8' }}> · {it.city}</span> : null}
          </div>
        ))}
      </div>
    </div>
  )
}

function fmtEta(ms) {
  if (ms == null) return null
  if (ms < 1500) return 'almost done'
  const s = Math.round(ms / 1000)
  if (s < 90) return `~${s}s left`
  return `~${Math.round(s / 60)}m left`
}

// One-line summary of the last warm run: status (running / INCOMPLETE / done),
// duration, warmed/failed, and a per-provider tally — the tell for a systemic
// failure. `completed:false` (or a stuck `running`) means the pass didn't finish.
// While a run is in progress we show a live % + progress bar + ETA (the cron
// persists progress every few seconds; the modal polls to refresh it).
function LastWarmSummary({ run }) {
  if (!run) return null
  const prov = run.byProvider || {}
  const provStr = Object.entries(prov).map(([k, v]) => `${k} ${v.warmed}✓${v.failed ? '/' + v.failed + '✗' : ''}`).join('  ')
  const mins = run.durationMs != null ? Math.round(run.durationMs / 60000) : null
  const bad = run.status === 'error' || run.status === 'stuck' || run.completed === false
  const color = run.status === 'running' ? '#b45309' : bad ? '#b91c1c' : '#166534'
  // A friendly name for the run: the live `active` marker carries `.kind`
  // (full/topup); historical markers just carry trigger.
  const kindLabel = run.kind === 'topup' ? 'Self-heal top-up'
    : run.kind === 'full' ? (run.trigger === 'manual' ? 'Manual full warm' : 'Nightly full warm')
    : `Last run (${run.trigger || 'cron'})`

  if (run.status === 'running') {
    const pct = run.pct != null ? run.pct : null
    const eta = fmtEta(run.etaMs)
    return (
      <span style={{ fontSize: 11.5, color: '#64748b', display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span>{kindLabel}: <b style={{ color }}>running{pct != null ? ` ${pct}%` : '…'}</b></span>
        {pct != null && (
          <span style={{ display: 'inline-block', width: 90, height: 6, background: '#fde68a', borderRadius: 3, overflow: 'hidden' }}>
            <span style={{ display: 'block', width: `${pct}%`, height: '100%', background: '#f59e0b', transition: 'width .4s' }} />
          </span>
        )}
        {run.coursesDone != null && run.courses != null && <span>{run.coursesDone}/{run.courses} courses</span>}
        {run.warmed != null && <span>· {run.warmed} warmed</span>}
        {eta && <span>· {eta}</span>}
      </span>
    )
  }

  const label = run.status === 'stuck' ? 'STUCK (killed mid-run)'
    : run.status === 'error' ? 'ERROR'
    : run.completed === false ? 'INCOMPLETE (capped)' : 'completed'
  return (
    <span style={{ fontSize: 11.5, color: '#64748b' }}>
      {kindLabel}: <b style={{ color }}>{label}</b>
      {mins != null ? ` · ${mins} min` : ''}
      {run.warmed != null ? ` · ${run.warmed} warmed, ${run.staleFlagged || 0} failed${run.timeouts ? `, ${run.timeouts} timeout` : ''}` : ''}
      {provStr ? <> · <span style={{ color: '#475569' }}>{provStr}</span></> : null}
    </span>
  )
}

function CacheDetailModal({ onClose }) {
  const [health, setHealth] = useState(null)
  const [detail, setDetail] = useState(null)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [savingHour, setSavingHour] = useState(false)

  async function toggleWarm(next) {
    setBusy(true)
    try {
      const { data } = await tournamentApi.put('/admin/course-integrations/tee-cache/warm', { enabled: next })
      setHealth(h => ({ ...h, warm: { ...h.warm, dbEnabled: data.dbEnabled, enabled: data.enabled } }))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setBusy(false) }
  }

  async function saveHour(hour) {
    setSavingHour(true)
    try {
      const { data } = await tournamentApi.put('/admin/course-integrations/tee-cache/warm-hour', { hour })
      setHealth(h => ({ ...h, warm: { ...h.warm, nightlyHour: data.nightlyHour } }))
    } catch (e) { alert(e?.response?.data?.message || e.message) }
    finally { setSavingHour(false) }
  }

  // Full cache state to a JSON file for troubleshooting — health + 7-day detail
  // (already in state) PLUS a deep per-problem-course export (providers with
  // secrets redacted, cache state, provider tally) fetched fresh, so the failing
  // pattern is visible from the file without any prod access.
  const [downloading, setDownloading] = useState(false)
  async function downloadHealth() {
    setDownloading(true)
    let diagnostics = null
    try { const { data } = await tournamentApi.get('/admin/course-integrations/tee-cache/diagnostics'); diagnostics = data }
    catch (e) { diagnostics = { error: e?.response?.data?.message || e.message } }
    const payload = { generatedAt: new Date().toISOString(), health, detail, diagnostics }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `tee-cache-diagnostics-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
    setDownloading(false)
  }

  // Trigger a full warm right now (background). The per-provider outcome lands
  // in health.warm.lastWarmRun; re-open the modal in a few minutes to read it.
  const [warmRunMsg, setWarmRunMsg] = useState('')
  const loadHealth = () =>
    tournamentApi.get('/admin/course-integrations/tee-cache/health')
      .then(r => setHealth(r.data))
      .catch(e => setErr(e?.response?.data?.message || e.message))

  async function runNow() {
    setWarmRunMsg('Starting…')
    try {
      const { data } = await tournamentApi.post('/admin/course-integrations/tee-cache/warm/run')
      setWarmRunMsg(data.message || 'Warm started.')
      // Give the background run a moment to write its first 'running' marker,
      // then refresh — that flips on the live-progress polling below.
      setTimeout(loadHealth, 1500)
    } catch (e) { setWarmRunMsg(e?.response?.data?.message || e.message) }
  }

  useEffect(() => {
    loadHealth()
    tournamentApi.get('/admin/course-integrations/tee-cache/detail').then(r => setDetail(r.data)).catch(() => {})
  }, [])

  // While a warm run is in progress, poll health every 5s so the % + ETA update
  // live. Stops as soon as the run finishes (status flips off 'running').
  // Poll whenever ANYTHING is actively warming — a manual/nightly full warm OR a
  // background self-heal top-up (health.warm.active). Not just the last manual run.
  const warmRunning = !!health?.warm?.active || health?.warm?.lastWarmRun?.status === 'running'
  useEffect(() => {
    if (!warmRunning) return
    const id = setInterval(loadHealth, 5000)
    return () => clearInterval(id)
  }, [warmRunning])

  const th = { padding: '4px 8px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: 12 }
  const td = { padding: '4px 8px', color: '#334155', fontSize: 12.5, borderTop: '1px solid #eef2f7' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 50, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 860, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Tee-time cache health</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {health && <button onClick={downloadHealth} disabled={downloading} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: downloading ? 'default' : 'pointer', opacity: downloading ? 0.6 : 1 }}>{downloading ? 'Preparing…' : 'Download JSON'}</button>}
            <button onClick={onClose} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Close</button>
          </div>
        </div>

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{err}</div>}
        {!health ? <div style={{ padding: 20, color: '#64748b' }}>Loading…</div> : (
          <>
            {/* Nightly-warm control */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '10px 12px', marginBottom: 14, borderRadius: 8,
              background: health.warm.enabled ? '#f0fdf4' : '#fffbeb', border: `1px solid ${health.warm.enabled ? '#bbf7d0' : '#fde68a'}` }}>
              <div style={{ fontSize: 13, lineHeight: 1.5 }}>
                <b style={{ color: health.warm.enabled ? '#166534' : '#92400e' }}>Nightly warm is {health.warm.enabled ? 'ON' : 'OFF'}.</b>{' '}
                {health.warm.enabled
                  ? <span style={{ color: '#475569' }}>Pre-warms all bookable courses × 7 days nightly at{' '}
                      <select value={health.warm.nightlyHour} disabled={health.warm.nightlyHourEnvForced || savingHour}
                        onChange={e => saveHour(Number(e.target.value))}
                        style={{ padding: '1px 4px', borderRadius: 4, border: '1px solid #cbd5e1', fontSize: 12.5,
                          cursor: (health.warm.nightlyHourEnvForced || savingHour) ? 'default' : 'pointer' }}>
                        {Array.from({ length: 24 }, (_, h) => <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>)}
                      </select>{' '}<span style={{ color: '#94a3b8' }}>server time{savingHour ? ' · saving…' : ''}{health.warm.nightlyHourEnvForced ? ' · set by server env' : ''}</span>.</span>
                  : <span style={{ color: '#92400e' }}>The cache only fills on-demand until you turn this on.</span>}
                {health.warm.envForced && <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>Forced on by the server env (TEE_CACHE_WARM_ENABLED) — toggle disabled.</div>}
              </div>
              <button onClick={() => toggleWarm(!health.warm.dbEnabled)} disabled={busy || health.warm.envForced}
                style={{ flexShrink: 0, padding: '8px 14px', borderRadius: 6, fontWeight: 600, fontSize: 13, cursor: (busy || health.warm.envForced) ? 'default' : 'pointer',
                  border: '1px solid ' + (health.warm.dbEnabled ? '#cbd5e1' : '#0f172a'),
                  background: health.warm.dbEnabled ? '#fff' : '#0f172a', color: health.warm.dbEnabled ? '#334155' : '#fff',
                  opacity: (busy || health.warm.envForced) ? 0.5 : 1 }}>
                {busy ? '…' : (health.warm.dbEnabled ? 'Turn OFF' : 'Turn ON')}
              </button>
            </div>

            {/* Run-now + last-run summary */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <button onClick={runNow} title="Run a full warm right now (background)"
                style={{ padding: '7px 12px', borderRadius: 6, fontWeight: 600, fontSize: 12.5, border: '1px solid #0f172a', background: '#fff', color: '#0f172a', cursor: 'pointer' }}>
                Run warm now
              </button>
              {warmRunMsg && <span style={{ fontSize: 12, color: '#2563eb' }}>{warmRunMsg}</span>}
              {/* Show the LIVE run if one is happening (full warm or self-heal top-up);
                  otherwise fall back to the last completed/stale run. This is what
                  keeps a stale "STUCK" marker from hiding an actively-running top-up. */}
              {health.warm.active
                ? <LastWarmSummary run={health.warm.active} />
                : (health.warm.lastWarmRun && <LastWarmSummary run={health.warm.lastWarmRun} />)}
            </div>

            {health.warm.stuck && !health.warm.active && (
              <div style={{ marginBottom: 14, padding: '9px 12px', borderRadius: 8, background: '#fef2f2', border: '1px solid #fecaca', fontSize: 12.5, color: '#991b1b' }}>
                ⚠ The last full warm was <b>killed mid-run</b> (often by a deploy/restart) and never finished — that's why coverage is stale.
                The hourly self-heal will refill it automatically; click <b>Run warm now</b> to recover immediately.
              </div>
            )}
            {health.warm.enabled && (
              <div style={{ fontSize: 11.5, color: '#94a3b8', marginBottom: 14 }}>
                Last full warm: {health.warm.lastCompletedAt ? fmt(health.warm.lastCompletedAt) : '—'}
                {health.warm.lastTopup ? <> · Self-heal top-up: {fmt(health.warm.lastTopup.finishedAt)} ({health.warm.lastTopup.warmed || 0} cells)</> : ' · Self-heal runs hourly'}
              </div>
            )}

            {/* Top stat row */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(140px,1fr))', gap: 10, marginBottom: 16 }}>
              <Stat label="Nightly warm" value={health.warm.enabled ? 'ON' : 'OFF'} sub={health.warm.enabled ? `runs ${String(health.warm.nightlyHour).padStart(2, '0')}:00 · last ${fmt(health.warm.lastWarmAt)}` : `set TEE_CACHE_WARM_ENABLED=1`} color={health.warm.enabled ? '#166534' : '#b45309'} />
              <Stat label="Coverage" value={health.coverage.coveragePct != null ? `${health.coverage.coveragePct}%` : '—'} sub={`${health.coverage.cachedCourses}/${health.coverage.bookableCourses} courses · ${health.coverage.cells} cells`} />
              <Stat label="Freshness" value={`${health.coverage.fresh} fresh`} sub={`${health.coverage.stale} stale · updated ${fmt(health.coverage.lastUpdatedAny)}`} />
              <Stat label="Hit rate (today)" value={health.today.hitRate != null ? `${health.today.hitRate}%` : '—'} sub={`${health.today.calls} calls · avg fill ${health.today.avgFillMs != null ? health.today.avgFillMs + 'ms' : '—'}`} />
            </div>
            <div style={{ fontSize: 12.5, color: '#475569', marginBottom: 16 }}>
              Today: <b style={{ color: OUTCOME_COLOR.hit }}>{health.today.hits} hits</b> · <b style={{ color: OUTCOME_COLOR.miss }}>{health.today.misses} misses</b> · <b style={{ color: OUTCOME_COLOR.stale }}>{health.today.stale} stale</b> · <b style={{ color: OUTCOME_COLOR.fresh }}>{health.today.fresh} fresh checks</b>
              {health.zeroPriceCourses > 0 && <> · <span style={{ color: '#b45309' }}>{health.zeroPriceCourses} $0 course{health.zeroPriceCourses === 1 ? '' : 's'}</span></>}
            </div>

            {/* tee-time.com throttle telemetry — how hard the provider is rate-limiting us
                (since the last deploy). Retries mean most throttles recover silently. */}
            {health.providerStats?.['teetime-com'] && (() => {
              const s = health.providerStats['teetime-com']
              if (!s.requests) return null
              const pct = Math.round((s.throttled / s.requests) * 100)
              const since = (() => { try { return new Date(s.since).toLocaleString('en-CA', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' }) } catch { return '' } })()
              return (
                <div style={{ fontSize: 12, color: s.hardFailed > 0 ? '#b45309' : '#64748b', marginBottom: 16 }}>
                  {s.advertisedLimit != null && (
                    <div style={{ marginBottom: 3 }}>
                      tee-time.com advertised limit: <b style={{ color: '#0f172a' }}>{s.advertisedLimit}/min</b>
                      {s.advertisedRemaining != null && <span style={{ color: '#94a3b8' }}> · {s.advertisedRemaining} remaining</span>}
                      {s.lastRetryAfter != null && <span style={{ color: '#94a3b8' }}> · last Retry-After {s.lastRetryAfter}s</span>}
                    </div>
                  )}
                  tee-time.com throttling (since {since}): <b>{s.requests}</b> requests · <b style={{ color: s.throttled ? '#b45309' : '#166534' }}>{s.throttled} throttled ({pct}%)</b>
                  {' · '}<span style={{ color: '#166534' }}>{s.recovered} recovered by retry</span>
                  {' · '}<span style={{ color: s.hardFailed ? '#b91c1c' : '#94a3b8' }}>{s.hardFailed} hard-failed</span>
                </div>
              )
            })()}

            {/* Problem breakdown — WHICH courses are behind the coverage/quality gap */}
            {health.problems && (health.problems.uncovered.length + health.problems.stale.length + health.problems.zeroPrice.length + (health.problems.needsConfig?.length || 0)) > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Problems to fix</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(230px,1fr))', gap: 10 }}>
                  <ProblemList title="Needs config (no rate code)" hint="TTC course with no rate code set — warm skips it. Set a rate on the course's Edit panel." items={health.problems.needsConfig} color="#7c3aed" />
                  <ProblemList title="Not cached" hint="Bookable, but the warm stored no tee times" items={health.problems.uncovered} color="#b91c1c" />
                  <ProblemList title="Not warming (stale)" hint="Has cached cells but none are fresh" items={health.problems.stale} color="#b45309" />
                  <ProblemList title="$0 / unpriced" hint="Live sheet returned a $0 green fee" items={health.problems.zeroPrice} color="#b45309" />
                </div>
              </div>
            )}

            {/* 7-day trend */}
            {detail?.series?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Last 7 days</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead><tr><th style={th}>Date</th><th style={th}>Hits</th><th style={th}>Misses</th><th style={th}>Stale</th><th style={th}>Fresh</th><th style={th}>Avg fill</th></tr></thead>
                  <tbody>{detail.series.map(s => (
                    <tr key={s.date}><td style={td}>{fmt(s.date).split(',')[0]}</td><td style={{ ...td, color: OUTCOME_COLOR.hit }}>{s.hits}</td><td style={{ ...td, color: OUTCOME_COLOR.miss }}>{s.misses}</td><td style={{ ...td, color: OUTCOME_COLOR.stale }}>{s.stale}</td><td style={{ ...td, color: OUTCOME_COLOR.fresh }}>{s.fresh}</td><td style={td}>{s.avgFillMs != null ? s.avgFillMs + 'ms' : '—'}</td></tr>
                  ))}</tbody>
                </table>
              </div>
            )}

            {/* Per-course */}
            {detail?.courses?.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Busiest courses (7d)</div>
                <div style={{ maxHeight: 240, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 6 }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead><tr><th style={th}>Course</th><th style={th}>Calls</th><th style={th}>Hit%</th><th style={th}>Miss</th><th style={th}>Cells</th><th style={th}>Fresh</th><th style={th}>Updated</th></tr></thead>
                    <tbody>{detail.courses.map(c => (
                      <tr key={c.coursesId}>
                        <td style={td}>{c.courseName || `#${c.coursesId}`}</td>
                        <td style={td}>{c.calls}</td>
                        <td style={{ ...td, color: c.hitRate == null ? '#94a3b8' : c.hitRate >= 80 ? OUTCOME_COLOR.hit : c.hitRate >= 50 ? '#b45309' : OUTCOME_COLOR.stale }}>{c.hitRate == null ? '—' : c.hitRate + '%'}</td>
                        <td style={td}>{c.misses}</td>
                        <td style={td}>{c.cells}</td>
                        <td style={td}>{c.freshCells}</td>
                        <td style={td}>{fmt(c.lastUpdated)}</td>
                      </tr>
                    ))}</tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Recent live calls */}
            {detail?.recent?.length > 0 && (
              <div>
                <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Recent calls (this server)</div>
                <div style={{ maxHeight: 160, overflowY: 'auto', fontSize: 12, fontFamily: 'monospace', color: '#475569' }}>
                  {detail.recent.map((r, i) => (
                    <div key={i} style={{ padding: '1px 0' }}>
                      <span style={{ color: '#94a3b8' }}>{fmt(r.at).split(', ')[1] || ''}</span>{' '}
                      <span style={{ color: OUTCOME_COLOR[r.outcome] || '#334155', fontWeight: 600 }}>{r.outcome}</span>{' '}
                      {r.courseName}{r.ms ? ` (${r.ms}ms)` : ''}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

function Stat({ label, value, sub, color }) {
  return (
    <div style={{ padding: '10px 12px', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <div style={{ fontSize: 11.5, color: '#64748b' }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 700, color: color || '#0f172a' }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

const CHECK_COLOR = { ok: '#166534', warn: '#b45309', error: '#b91c1c', info: '#475569' }
const CHECK_BG = { ok: '#f0fdf4', warn: '#fffbeb', error: '#fef2f2', info: '#f8fafc' }

// Server environment / config snapshot. Secret values are masked server-side
// (only operational flags come through in full); this modal never receives raw
// secrets. Highlights key checks (NODE_ENV, fake-payments), then the full masked
// var table with a filter and a Download JSON button for troubleshooting.
function EnvModal({ onClose }) {
  const [env, setEnv] = useState(null)
  const [err, setErr] = useState('')
  const [filter, setFilter] = useState('')

  useEffect(() => {
    tournamentApi.get('/admin/course-integrations/env')
      .then(r => setEnv(r.data)).catch(e => setErr(e?.response?.data?.message || e.message))
  }, [])

  function download() {
    const payload = env || { error: err }
    const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url; a.download = `env-snapshot-${new Date().toISOString().slice(0, 10)}.json`
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url)
  }

  const q = filter.trim().toLowerCase()
  const rows = (env?.env || []).filter(r => !q || r.key.toLowerCase().includes(q))
  const th = { padding: '4px 8px', fontWeight: 600, color: '#64748b', textAlign: 'left', fontSize: 12 }
  const td = { padding: '4px 8px', color: '#334155', fontSize: 12.5, borderTop: '1px solid #eef2f7', verticalAlign: 'top' }

  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: 24, zIndex: 50, overflowY: 'auto' }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 780, boxShadow: '0 10px 40px rgba(0,0,0,0.2)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
          <h3 style={{ margin: 0, fontSize: 17 }}>Environment</h3>
          <div style={{ display: 'flex', gap: 8 }}>
            {env && <button onClick={download} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Download JSON</button>}
            <button onClick={onClose} style={{ padding: '6px 12px', border: '1px solid #cbd5e1', borderRadius: 6, background: '#fff', cursor: 'pointer' }}>Close</button>
          </div>
        </div>
        <p style={{ margin: '0 0 14px', fontSize: 11.5, color: '#94a3b8', lineHeight: 1.5 }}>
          Secret values are masked (shown as <code>ab••••yz · N chars</code>) and never leave the server. Only non-secret operational flags are shown in full.
        </p>

        {err && <div style={{ padding: 10, background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>{err}</div>}
        {!env && !err ? <div style={{ padding: 20, color: '#64748b' }}>Loading…</div> : env && (
          <>
            <div style={{ fontSize: 12, color: '#64748b', marginBottom: 12 }}>
              Host <b style={{ color: '#334155' }}>{env.host}</b> · NODE_ENV <b style={{ color: env.nodeEnv === 'production' ? '#166534' : '#b45309' }}>{env.nodeEnv || '(unset)'}</b> · {env.count} vars
            </div>

            {/* Key checks */}
            {env.checks?.length > 0 && (
              <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
                {env.checks.map(c => (
                  <div key={c.name} style={{ padding: '8px 10px', borderRadius: 6, background: CHECK_BG[c.level] || '#f8fafc', border: `1px solid ${CHECK_COLOR[c.level] || '#e2e8f0'}22` }}>
                    <div style={{ fontSize: 12.5 }}>
                      <b style={{ color: '#0f172a' }}>{c.name}</b> = <code style={{ color: '#334155' }}>{c.value}</code>
                    </div>
                    <div style={{ fontSize: 11.5, color: CHECK_COLOR[c.level] || '#64748b', marginTop: 2 }}>{c.note}</div>
                  </div>
                ))}
              </div>
            )}

            {/* Full var table */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>All variables</div>
              <input value={filter} onChange={e => setFilter(e.target.value)} placeholder="filter by name…"
                style={{ padding: '4px 8px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 12.5, width: 180 }} />
            </div>
            <div style={{ maxHeight: 360, overflowY: 'auto', border: '1px solid #eef2f7', borderRadius: 6 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead><tr><th style={th}>Variable</th><th style={th}>Value</th></tr></thead>
                <tbody>{rows.map(r => (
                  <tr key={r.key}>
                    <td style={{ ...td, fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {r.key}
                      {r.secret && <span title="masked secret" style={{ marginLeft: 6, fontSize: 10, color: '#b45309' }}>🔒</span>}
                    </td>
                    <td style={{ ...td, fontFamily: 'monospace', color: r.masked ? '#94a3b8' : '#0f172a', wordBreak: 'break-all' }}>{r.value}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
            {rows.length === 0 && <div style={{ padding: 12, color: '#94a3b8', fontSize: 12.5 }}>No variables match “{filter}”.</div>}
          </>
        )}
      </div>
    </div>
  )
}
